/* AT2 EXPERIMENT - API Utils */

import type { DossierId } from '../../../../../../../resources/dossier/types';
import { ApiClient, HttpError } from '../../../../../../../resources/shared/api/client';
import { getEnvRequired } from '../../../../../../../shared/utilities/env';
import type { AT2Analysis } from '../mock/at2-mock-api';

const MAX_RETRIES = 3;
const TIMEOUT_MS = 29000;

const REQUEST_MANAGER_API_URL = getEnvRequired('REQUEST_MANAGER_API_URL');
const apiClient = new ApiClient(true);

/**
 * *********************************************************************************
 * * Analysis API
 * *********************************************************************************
 */

export type AnalysisResult =
    | { success: true; analysis: AT2Analysis }
    | { success: false; error: string };

type AnalyzeMessageAT2Response = {
    needsOptimization: boolean;
    missingElements: string[];
};

const buildAnalyzeEndpoint = (dossierId: DossierId): string =>
    `${REQUEST_MANAGER_API_URL}/experiments/at2-analyze/${dossierId}`;

const analyzeMessageAT2 = async (
    dossierId: DossierId,
    agentMessage: string,
    locale: string = 'fr',
): Promise<AnalyzeMessageAT2Response> => {
    const url = buildAnalyzeEndpoint(dossierId);

    return apiClient.post<AnalyzeMessageAT2Response>(
        url,
        JSON.stringify({ agentMessage, locale }),
        {
            'Content-Type': 'application/json',
        },
    );
};

export const callAnalyzeAPI = async (
    dossierId: DossierId,
    message: string,
    locale: string = 'fr-FR',
): Promise<AnalysisResult> => {
    const [oneStringLocale] = locale.split('-');
    try {
        const analysis = await retryWithTimeout(() =>
            analyzeMessageAT2(dossierId, message, oneStringLocale),
        );
        return { success: true, analysis };
    } catch (error) {
        if (error instanceof HttpError) {
            console.error('[AT2 API] Analysis error after retries:', {
                status: error.status,
                body: error.body,
                message: error.message,
            });
        } else {
            console.error('[AT2 API] Analysis error after retries:', error);
        }

        return { success: false, error: "Erreur lors de l'analyse" };
    }
};

/**
 * *********************************************************************************
 * * Generation API
 * *********************************************************************************
 */

export type GenerationResult =
    | { success: true; improvedMessage: string }
    | { success: false; error: string };

type GenerateMessageAT2Response = {
    improvedMessage: string;
};

const buildGenerateEndpoint = (dossierId: DossierId): string =>
    `${REQUEST_MANAGER_API_URL}/experiments/at2-generate/${dossierId}`;

const generateMessageAT2 = async (
    dossierId: DossierId,
    agentMessage: string,
    locale: string,
    missingElements: string[],
): Promise<GenerateMessageAT2Response> => {
    const url = buildGenerateEndpoint(dossierId);

    return apiClient.post<GenerateMessageAT2Response>(
        url,
        JSON.stringify({ agentMessage, locale, missingElements }),
        {
            'Content-Type': 'application/json',
        },
    );
};

export const callGenerateAPI = async (
    dossierId: DossierId,
    originalMessage: string,
    analysis: AT2Analysis,
    locale: string = 'fr-FR',
): Promise<GenerationResult> => {
    const [oneStringLocale] = locale.split('-');
    const missingElements = analysis.missingElements || [];
    try {
        const { improvedMessage } = await retryWithTimeout(() =>
            generateMessageAT2(dossierId, originalMessage, oneStringLocale, missingElements),
        );
        return { success: true, improvedMessage };
    } catch (error) {
        if (error instanceof HttpError) {
            console.error('[AT2 API] Generation error after retries:', {
                status: error.status,
                body: error.body,
                message: error.message,
            });
        } else {
            console.error('[AT2 API] Generation error after retries:', error);
        }
        return { success: false, error: 'Erreur lors de la génération' };
    }
};

/**
 * *********************************************************************************
 * * Shadow Analyze and Generate API
 * *********************************************************************************
 */

type ShadowAnalyzeAndGenerateResult =
    | { success: true }
    | { success: false; errorMessage: string; error: unknown };

const buildShadowAnalyzeEndpoint = (dossierId: DossierId): string =>
    `${REQUEST_MANAGER_API_URL}/experiments/at2-analyze/${dossierId}`;

const shadowAnalyzeMessageAT2 = async (
    dossierId: DossierId,
    agentMessage: string,
    locale: string,
): Promise<void> => {
    const url = buildShadowAnalyzeEndpoint(dossierId);

    return apiClient.post<void>(url, JSON.stringify({ agentMessage, locale }), {
        'Content-Type': 'application/json',
    });
};

export const callShadowAnalyzeAPI = async (
    dossierId: DossierId,
    message: string,
    locale: string = 'fr-FR',
): Promise<ShadowAnalyzeAndGenerateResult> => {
    const [oneStringLocale] = locale.split('-');
    try {
        await shadowAnalyzeMessageAT2(dossierId, message, oneStringLocale);
        return { success: true };
    } catch (error) {
        if (error instanceof HttpError) {
            console.error('[AT2 API] Shadow Analyze and Generate error after retries:', {
                status: error.status,
                body: error.body,
                message: error.message,
            });
        } else {
            console.error('[AT2 API] Shadow Analyze and Generate error after retries:', error);
        }
        return {
            success: false,
            errorMessage: "Erreur lors de l'analyse et de la génératio en mode shadow",
            error,
        };
    }
};

/**
 * *********************************************************************************
 * * Utilities functions
 * *********************************************************************************
 */

/**
 * Wrapper pour ajouter un timeout à une promise
 */
function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => setTimeout(() => reject(new Error('Timeout')), timeoutMs)),
    ]);
}

/**
 * Retry logic générique avec timeout
 * Effectue jusqu'à maxRetries tentatives avec timeout par tentative
 */
async function retryWithTimeout<T>(
    fn: () => Promise<T>,
    maxRetries: number = MAX_RETRIES,
    timeoutMs: number = TIMEOUT_MS,
): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await withTimeout(fn(), timeoutMs);
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            console.warn(`[AT2 API] Attempt ${attempt}/${maxRetries} failed:`, lastError.message);

            // Si c'est la dernière tentative, on throw
            if (attempt === maxRetries) {
                throw lastError;
            }

            // Petite pause avant retry (100ms * numéro de tentative)
            await new Promise((resolve) => setTimeout(resolve, 100 * attempt));
        }
    }

    throw lastError || new Error('Retry failed');
}
